import { useCallback, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { MessageSquare, Plus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, Field, FormError, SubmitButton } from "@/components/common";
import { Thread, ConvRow, usePoll } from "@/modules/messaging/Thread";
import { useFetch } from "@/lib/useFetch";
import { api, errMsg } from "@/lib/api";
import { t } from "@/i18n";

const brand = { background: "var(--brand)", color: "var(--brand-contrast)" };

function NewQuestion({ slug, onCreated, onCancel }) {
  const vehicles = useFetch(`/portal/${slug}/vehicles`);
  const [f, setF] = useState({ vehicle_id: "", text: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/portal/${slug}/messages`, f);
      onCreated(data.id);
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  const list = vehicles.data || [];
  return (
    <form onSubmit={submit} className="space-y-3 p-4" data-testid="portal-new-message-form">
      <FormError message={error} />
      <p className="text-sm text-slate-600">{list.length || !vehicles.data ? t("b9.msg.portalNewHint") : t("b9.msg.addVehicleFirst")}</p>
      <div className="grid gap-2 sm:grid-cols-2">
        {list.map((v) => (
          <button key={v.id} type="button" onClick={() => setF({ ...f, vehicle_id: v.id })} data-testid={`portal-new-message-vehicle-${v.plate}`}
            className={`rounded-xl border-2 p-3 text-left text-sm transition-colors duration-150 ${f.vehicle_id === v.id ? "border-[var(--brand)] bg-white shadow-sm" : "border-slate-200 bg-white hover:border-slate-300"}`}>
            <p className="font-mono-plate font-semibold">{v.plate}</p><p className="text-slate-600">{v.make} {v.model}</p>
          </button>
        ))}
      </div>
      <Field label={t("msg.message")}><Textarea required value={f.text} onChange={(e) => setF({ ...f, text: e.target.value })} data-testid="portal-new-message-text" /></Field>
      <div className="flex gap-2"><SubmitButton busy={busy} disabled={!f.vehicle_id} style={brand} data-testid="portal-new-message-submit">{t("msg.send")}</SubmitButton><Button type="button" variant="ghost" onClick={onCancel}>{t("common.cancel")}</Button></div>
    </form>
  );
}

export function MessagesTab({ slug, tz }) {
  const [params, setParams] = useSearchParams();
  const [list, setList] = useState(null);
  const active = params.get("c");
  const go = (p) => setParams({ tab: "messages", ...p });
  const load = useCallback(() => api.get(`/portal/${slug}/messages`).then((r) => setList(r.data.items)).catch((e) => toast.error(errMsg(e))), [slug]);
  usePoll(load);
  return (
    <div className="gf-card grid overflow-hidden md:grid-cols-[300px_1fr]" data-testid="portal-messages">
      <div className={`border-r border-slate-200 ${active || params.get("new") ? "hidden md:block" : ""}`}>
        <div className="border-b border-slate-200 p-3"><Button size="sm" style={brand} onClick={() => go({ new: "1" })} data-testid="portal-new-message-button"><Plus className="mr-1 h-4 w-4" /> {t("msg.newQuestion")}</Button></div>
        {list && list.length === 0 && <p className="p-4 text-sm text-slate-500">{t("msg.portalEmpty")}</p>}
        {(list || []).map((c) => <ConvRow key={c.id} c={c} active={c.id === active} onClick={() => go({ c: c.id })} tz={tz} />)}
      </div>
      <div className={active || params.get("new") ? "" : "hidden md:block"}>
        {params.get("new") ? <NewQuestion slug={slug} onCancel={() => go({})} onCreated={(id) => { go({ c: id }); load(); }} />
          : active ? <>
            <button type="button" className="px-4 pt-3 text-sm text-slate-600 md:hidden" onClick={() => go({})} data-testid="portal-thread-back">← {t("msg.title")}</button>
            <Thread key={active} path={`/portal/${slug}/messages/${active}`} sendPath={`/portal/${slug}/messages/${active}`} tz={tz} onChange={load} />
          </> : <p className="p-10 text-center text-sm text-slate-500">{t("msg.pick")}</p>}
      </div>
    </div>
  );
}

export function PortalLatestMessages({ slug, tz }) {
  const { data } = useFetch(`/portal/${slug}/messages`);
  if (!data) return null;
  return (
    <Card testId="portal-latest-messages" className="min-w-0">
      <MessageSquare className="h-5 w-5" style={{ color: "var(--brand)" }} />
      <p className="mt-3 font-display text-lg font-bold">{t("msg.title")} {data.unread > 0 && <span className="ml-1 rounded-full px-2 py-0.5 text-xs" style={brand} data-testid="portal-messages-unread">{data.unread}</span>}</p>
      {data.items.slice(0, 2).map((c) => <p key={c.id} className="mt-1 truncate text-sm text-slate-600">{c.subject}: {c.last_preview}</p>)}
      {data.items.length === 0 && <p className="mt-1 text-sm text-slate-600">{t("msg.portalEmpty")}</p>}
      <Button asChild size="sm" variant="outline" className="mt-4" data-testid="portal-open-messages"><Link to={`/g/${slug}/account?tab=messages`}>{t("msg.openMessages")}</Link></Button>
    </Card>
  );
}
